import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type BlockNoteEditor } from '@blocknote/core';
import { BlockNoteView } from '@blocknote/mantine';
import { useCreateBlockNote } from '@blocknote/react';
import { useSubjectLanguage } from '@features/user';
import {
  // Circle as CircleIcon,
  Close as CloseIcon,
  // History as HistoryIcon,
  Redo as RedoIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  // Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { capitalize } from '@utils/utils';

import { useActionEditor } from '../../store/hooks';
import { type EditModalContentProps, type PepCPayload } from '../../types';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';

/**
 * PEP-C specific edit modal content component
 * Follows the EditModalContentProps interface for consistency with other modals
 */
export const PepCEditModal: React.FC<EditModalContentProps> = ({
  context,
  onSave,
  onCancel,
  isLoading = false,
  error = null,
}) => {
  const { action } = context;
  const originalData = action.action_payload as PepCPayload;
  const actionId = action.action_id;

  // Use the editor hook with PepCPayload type for draft functionality
  const {
    draft,
    canUndo,
    canRedo,
    initializeDraft,
    handleContentChange,
    handleSave: prepareSave,
    undo: performUndo,
    redo: performRedo,
    clearDraft,
    // reset,
  } = useActionEditor<PepCPayload>(actionId);

  // Local state owns the truth during editing
  const initialData = draft?.data || originalData;
  const [localState, setLocalState] = useState<PepCPayload>(initialData);
  const [isEditorInitialized, setIsEditorInitialized] = useState(false);

  const subjectLanguage = useSubjectLanguage();
  const editorRef = useRef<BlockNoteEditor | null>(null);
  const lastSyncedDraft = useRef(draft);
  const isInitializingRef = useRef(true);
  const isUpdatingFromReduxRef = useRef(false);

  // Check if current state differs from original
  // const canReset = localState.title !== originalData.title || localState.body !== originalData.body;

  // Wrap undo/redo to work with local state
  const undo = useCallback(() => {
    performUndo();
    // Draft will update via useEffect, triggering local state update
  }, [performUndo]);

  const redo = useCallback(() => {
    performRedo();
    // Draft will update via useEffect, triggering local state update
  }, [performRedo]);

  // Initialize draft on mount
  useEffect(() => {
    // Set initializing flag first
    isInitializingRef.current = true;
    initializeDraft(originalData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync from Redux only when draft changes from external sources (undo/redo)
  useEffect(() => {
    if (!draft || draft === lastSyncedDraft.current) return;

    // This is from undo/redo or external source - update local state
    setLocalState(draft.data);
    lastSyncedDraft.current = draft;
  }, [draft]); // Only watch draft changes

  // Create the BlockNote editor
  const editor = useCreateBlockNote({
    initialContent: undefined,
  });

  // Store editor reference
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Initialize editor content only once
  useEffect(() => {
    if (isEditorInitialized) return;

    const initializeEditor = async () => {
      const content = localState.body;
      try {
        const blocks = await editor.tryParseMarkdownToBlocks(content);
        editor.replaceBlocks(editor.document, blocks);
        setIsEditorInitialized(true);
      } catch (err) {
        console.error('Error initializing editor:', err);
      }
    };

    void initializeEditor();
  }, [editor, localState.body, isEditorInitialized]); // Include all dependencies

  // Clear initialization flag after editor is ready
  useEffect(() => {
    if (isEditorInitialized) {
      // Use requestAnimationFrame to ensure the editor has finished its internal updates
      requestAnimationFrame(() => {
        isInitializingRef.current = false;
      });
    }
  }, [isEditorInitialized]);

  // Update editor when local state changes (from undo/redo)
  useEffect(() => {
    if (!isEditorInitialized) return;

    const updateEditor = async () => {
      try {
        const currentMarkdown = await editor.blocksToMarkdownLossy(editor.document);
        // Only update if content is different
        if (currentMarkdown !== localState.body) {
          isUpdatingFromReduxRef.current = true;
          const blocks = await editor.tryParseMarkdownToBlocks(localState.body);
          editor.replaceBlocks(editor.document, blocks);
          // Reset flag after a frame to ensure change event has fired
          requestAnimationFrame(() => {
            isUpdatingFromReduxRef.current = false;
          });
        }
      } catch (err) {
        console.error('Error updating editor:', err);
      }
    };

    void updateEditor();
  }, [localState.body, editor, isEditorInitialized]);

  // Handle title changes
  const handleTitleChange = useCallback(
    (value: string) => {
      // Update local state immediately for responsive UI
      setLocalState(prev => ({ ...prev, title: value }));
      // Sync to Redux (debounced)
      handleContentChange({ title: value });
    },
    [handleContentChange],
  );

  // Handle editor content changes
  const handleEditorChange = useCallback(async () => {
    if (!editorRef.current) return;

    // Don't process changes during initialization
    if (isInitializingRef.current) {
      return;
    }

    // Don't process changes when we're updating from Redux (undo/redo)
    if (isUpdatingFromReduxRef.current) {
      return;
    }

    try {
      const markdown = await editorRef.current.blocksToMarkdownLossy(editorRef.current.document);
      // Update local state immediately
      setLocalState(prev => ({ ...prev, body: markdown }));
      // Sync to Redux (debounced)
      handleContentChange({ body: markdown });
    } catch (error) {
      console.error('Error converting to markdown:', error);
    }
  }, [handleContentChange]);

  const handleSave = useCallback(async () => {
    try {
      // Save draft snapshot before saving
      await prepareSave();

      // Use local state which is the source of truth
      const updatedData: PepCPayload = {
        ...originalData,
        title: localState.title,
        body: localState.body,
      };

      onSave(updatedData as unknown as Record<string, unknown>);

      // Clear draft after successful save
      clearDraft();
    } catch (error) {
      console.error('Error saving message:', error);
    }
  }, [prepareSave, originalData, localState, onSave, clearDraft]);

  // Handle reset - commented out for now
  // const handleReset = useCallback(() => {
  //   if (confirm('Are you sure you want to reset? This will discard all unsaved changes.')) {
  //     // Reset Redux state
  //     reset(originalData);
  //     // Reset local state
  //     setLocalState({
  //       title: originalData.title,
  //       body: originalData.body,
  //     });
  //   }
  // }, [reset, originalData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + Z for undo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) {
          undo();
        }
      }

      // Cmd/Ctrl + Shift + Z for redo
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        if (canRedo) {
          redo();
        }
      }

      // Cmd/Ctrl + S for save
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        void handleSave();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canUndo, canRedo, undo, redo, handleSave]);

  return (
    <>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Edit {capitalize(subjectLanguage)} Communication
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        <Stack spacing={0} sx={{ height: '100%' }}>
          {/* Title Field */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Message Title"
                  value={localState.title}
                  onChange={e => {
                    handleTitleChange(e.target.value);
                  }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled={isLoading}
                />
                {/* Draft Status Indicator - Commented out for now */}
                {/* {draft && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
                    <CircleIcon
                      sx={{
                        fontSize: 8,
                        color: draft.isDirty ? 'warning.main' : 'success.main'
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {draft.isDirty ? 'Unsaved changes' : 'All changes saved'}
                    </Typography>
                  </Stack>
                )} */}

                {/* Undo/Redo Buttons */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <Tooltip title="Undo (Cmd+Z)" placement="bottom">
                    <span>
                      <IconButton size="small" onClick={undo} disabled={!canUndo}>
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Redo (Cmd+Shift+Z)" placement="bottom">
                    <span>
                      <IconButton size="small" onClick={redo} disabled={!canRedo}>
                        <RedoIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>

              {/* Action Buttons Row - Commented out for now */}
              {/* <Stack direction="row" spacing={1} alignItems="center">
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Button
                  size="small"
                  variant="text"
                  onClick={handleReset}
                  disabled={!canReset}
                  startIcon={<HistoryIcon fontSize="small" />}
                  title={canReset ? "Reset to original content" : "No changes to reset"}
                >
                  Reset
                </Button>
                {draft && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    Last saved: {new Date(draft.lastSaved).toLocaleTimeString()}
                  </Typography>
                )}
              </Stack> */}
            </Stack>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Editor Container */}
          <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            <Alert severity="info" sx={{ m: 2, mb: 0 }}>
              <Typography variant="body2">
                This message will be sent to the {subjectLanguage} via SMS.
              </Typography>
            </Alert>

            <Box
              sx={{
                height: 'calc(100% - 80px)',
                overflow: 'auto',
                '& .bn-container': {
                  height: '100%',
                  border: 'none',
                  borderRadius: 0,
                },
                '& .bn-editor': {
                  paddingTop: '1rem',
                  paddingBottom: '1rem',
                },
              }}
            >
              <BlockNoteView
                editor={editor}
                theme="light"
                sideMenu={false}
                onChange={handleEditorChange}
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isLoading || !localState.title.trim()}
        >
          {isLoading ? 'Saving...' : 'Save & Send'}
        </Button>
      </DialogActions>
    </>
  );
};
